/**
 * DOM Observers utility functions for efficient element watching
 * Senior-level approach using browser APIs instead of polling
 */

export interface WaitForElementOptions {
    timeout?: number;
    checkExisting?: boolean;
}

export interface WaitForVisibilityOptions {
    timeout?: number;
    threshold?: number;
}

/**
 * Waits for an element to appear in the DOM using MutationObserver
 * Much more efficient than polling with setTimeout
 */
export const waitForElement = (selector: string, options: WaitForElementOptions = {}): Promise<Element> => {
    const { timeout = 5000, checkExisting = true } = options;

    return new Promise((resolve, reject) => {
        // Check if element already exists
        if (checkExisting) {
            const existing = document.querySelector(selector);
            if (existing) {
                resolve(existing);
                return;
            }
        }

        let observer: MutationObserver | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        const cleanup = () => {
            if (observer) {
                observer.disconnect();
                observer = null;
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };

        // Set timeout
        timeoutId = setTimeout(() => {
            cleanup();
            reject(new Error(`Element "${selector}" not found within ${timeout}ms`));
        }, timeout);

        // Create MutationObserver to watch for DOM changes
        observer = new MutationObserver(() => {
            // Check for the element after each mutation
            const element = document.querySelector(selector);
            if (element) {
                cleanup();
                resolve(element);
            }
        });

        // Start observing
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false,
            attributeOldValue: false,
            characterData: false,
            characterDataOldValue: false,
        });
    });
};

/**
 * Waits for an element to become visible using IntersectionObserver
 * Handles cases where element exists but is not visible (display: none, etc.)
 */
export const waitForElementVisibility = (element: Element, options: WaitForVisibilityOptions = {}): Promise<void> => {
    const { timeout = 5000, threshold = 0 } = options;

    return new Promise((resolve, reject) => {
        let observer: IntersectionObserver | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        const cleanup = () => {
            if (observer) {
                observer.disconnect();
                observer = null;
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };

        // Set timeout
        timeoutId = setTimeout(() => {
            cleanup();
            reject(new Error(`Element not visible within ${timeout}ms`));
        }, timeout);

        // Check if element is already visible
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            cleanup();
            resolve();
            return;
        }

        // Create IntersectionObserver to watch for visibility
        observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry && entry.isIntersecting) {
                    cleanup();
                    resolve();
                }
            },
            {
                threshold,
                rootMargin: '0px',
            },
        );

        observer.observe(element);
    });
};

/**
 * Combined helper: wait for element to appear AND become visible
 * This is the most robust approach for onboarding targets
 */
export const waitForElementReady = async (
    selector: string,
    elementOptions: WaitForElementOptions = {},
    visibilityOptions: WaitForVisibilityOptions = {},
): Promise<Element> => {
    // Wait for element to exist in DOM
    const element = await waitForElement(selector, elementOptions);

    // Wait for element to be visible
    await waitForElementVisibility(element, visibilityOptions);

    return element;
};

/**
 * Waits for next animation frame (better than setTimeout for render sync)
 */
export const waitForNextFrame = (): Promise<void> => {
    return new Promise((resolve) => {
        requestAnimationFrame(() => resolve());
    });
};

/**
 * Waits for two animation frames (ensures element is fully rendered)
 */
export const waitForRender = (): Promise<void> => {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => resolve());
        });
    });
};

/**
 * Custom event dispatcher for modal lifecycle events
 */
export const dispatchModalEvent = (eventType: string, detail: Record<string, unknown> = {}): void => {
    const event = new CustomEvent(`onboarding-${eventType}`, {
        detail,
        bubbles: true,
        cancelable: false,
    });

    window.dispatchEvent(event);
};

/**
 * Listen for modal lifecycle events
 */
export const onModalEvent = (
    eventType: string,
    callback: (detail: unknown) => void,
    options: { once?: boolean } = {},
): (() => void) => {
    const handler = (event: CustomEvent) => {
        callback(event.detail);
    };

    const eventName = `onboarding-${eventType}`;
    window.addEventListener(eventName, handler as EventListener, options);

    // Return cleanup function
    return () => {
        window.removeEventListener(eventName, handler as EventListener);
    };
};

/**
 * Promise-based event listener
 */
export const waitForModalEvent = (eventType: string, timeout: number = 5000): Promise<unknown> => {
    return new Promise((resolve, reject) => {
        let timeoutId: NodeJS.Timeout | null = null;
        let cleanup: (() => void) | null = null;

        const handleTimeout = () => {
            if (cleanup) cleanup();
            reject(new Error(`Modal event "${eventType}" not received within ${timeout}ms`));
        };

        timeoutId = setTimeout(handleTimeout, timeout);

        cleanup = onModalEvent(
            eventType,
            (detail) => {
                if (timeoutId) clearTimeout(timeoutId);
                resolve(detail);
            },
            { once: true },
        );
    });
};
